function signDay(timestamp) {
  return new Date(timestamp + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export function beijingSignDate() {
  return signDay(Date.now());
}

export function historicalMakeupRange() {
  const today = Date.parse(beijingSignDate() + 'T00:00:00Z');
  return {
    start: new Date(today - 30 * 86400000).toISOString().slice(0, 10),
    end: new Date(today - 86400000).toISOString().slice(0, 10),
  };
}

export function isHistoricalMakeupDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(value + 'T00:00:00Z');
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) return false;
  const range = historicalMakeupRange();
  return value >= range.start && value <= range.end;
}

export function sameSignIntent(operation, intent) {
  const mode = value => value && value.mode === 'historical_makeup' ? 'historical_makeup' : 'normal';
  if (mode(operation) !== mode(intent)) return false;
  return mode(intent) === 'normal' || (operation.signType === intent.signType && operation.signDate === intent.signDate);
}

export function isSameSignDay(timestamp) {
  return Number.isFinite(timestamp) && Math.abs(timestamp) <= 8640000000000000 - 8 * 60 * 60 * 1000 &&
    signDay(timestamp) === signDay(Date.now());
}

export function createSignPendingStore(key) {
  let memory = null;
  return {
    read() {
      try { localStorage.removeItem(key); } catch (e) { /* Legacy records have no operation ID. */ }
      try {
        const raw = sessionStorage.getItem(key);
        memory = raw ? JSON.parse(raw) : null;
      } catch (e) { /* Use memory if browser storage is unavailable. */ }
      return memory;
    },
    save(pending) {
      memory = pending;
      try { sessionStorage.setItem(key, JSON.stringify(pending)); } catch (e) { /* Use memory for this page. */ }
    },
    clear() {
      memory = null;
      try { sessionStorage.removeItem(key); } catch (e) { /* Use memory for this page. */ }
    },
  };
}

function newOperationId() {
  const cryptoApi = typeof window !== 'undefined' && window.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createSignOperationManager(namespace, ttl) {
  const memory = Object.create(null);
  const storageKey = subject => `gxy_sign_operation:${namespace}:${encodeURIComponent(subject)}`;
  const write = (subject, operation) => {
    memory[subject] = operation;
    try { sessionStorage.setItem(storageKey(subject), JSON.stringify(operation)); } catch (e) { /* Storage may be disabled. */ }
  };
  const clear = subject => {
    delete memory[subject];
    try { sessionStorage.removeItem(storageKey(subject)); } catch (e) { /* Storage may be disabled. */ }
  };
  const read = subject => {
    let operation = memory[subject];
    try {
      const raw = sessionStorage.getItem(storageKey(subject));
      if (raw) operation = JSON.parse(raw);
    } catch (e) { /* Keep the in-memory operation when storage is unavailable. */ }
    if (!operation) return null;
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(operation.id || '') ||
        !Number.isInteger(operation.step) || operation.step < 0 ||
        !['requesting', 'waiting_proxy', 'retryable', 'awaiting_auth', 'success', 'failed'].includes(operation.state) ||
        !isSameSignDay(operation.createdAt) ||
        (operation.state !== 'waiting_proxy' && Date.now() -
          (operation.proxyWaitActivityAt || operation.createdAt) > ttl) ||
        Date.now() < operation.createdAt ||
        (operation.mode && !['normal', 'historical_makeup'].includes(operation.mode)) ||
        (operation.mode === 'historical_makeup' &&
          (!['START', 'END'].includes(operation.signType) || !isHistoricalMakeupDate(operation.signDate)))) {
      clear(subject);
      return null;
    }
    return operation;
  };
  const matches = (subject, expected) => {
    const operation = read(subject);
    return Boolean(operation && expected && operation.id === expected.id && operation.step === expected.step);
  };
  return {
    read,
    matches,
    begin(subject, expected, intent = { mode: 'normal' }) {
      let operation = read(subject);
      if (expected && (!matches(subject, expected) || ['success', 'failed'].includes(operation.state))) return null;
      if (expected) intent = expected;
      if (intent.mode && !['normal', 'historical_makeup'].includes(intent.mode)) return null;
      if (intent.mode === 'historical_makeup' &&
          (!['START', 'END'].includes(intent.signType) || !isHistoricalMakeupDate(intent.signDate))) return null;
      const changedIntent = operation && !sameSignIntent(operation, intent);
      if (changedIntent && ['requesting', 'waiting_proxy', 'retryable'].includes(operation.state)) return null;
      if (!operation || ['success', 'failed'].includes(operation.state) || changedIntent) {
        operation = { id: newOperationId(), step: 0, createdAt: Date.now(), state: 'requesting' };
        if (intent.mode === 'historical_makeup') {
          Object.assign(operation, { mode: 'historical_makeup', signType: intent.signType, signDate: intent.signDate });
        }
      } else if (operation.state === 'awaiting_auth') {
        operation = { ...operation, step: operation.step + 1, state: 'requesting' };
      } else {
        operation = { ...operation, state: 'requesting',
          ...(operation.state === 'waiting_proxy' ? { proxyWaitActivityAt: Date.now() } : {}) };
      }
      write(subject, operation);
      return operation;
    },
    mark(subject, expected, state) {
      if (!matches(subject, expected)) return false;
      const previous = read(subject);
      const activity = previous.state === 'waiting_proxy' || state === 'waiting_proxy' || previous.proxyWaitActivityAt
        ? { proxyWaitActivityAt: Date.now() } : {};
      write(subject, { ...previous, ...activity, state });
      return true;
    },
  };
}
